import {
  Control,
  Controller,
  FieldValues,
  UseFormClearErrors,
  useFormContext,
  UseFormSetValue,
} from "react-hook-form";
import {
  forwardRef,
  SetStateAction,
  useEffect,
  useImperativeHandle,
  useState,
} from "react";
import { useFlightResults } from "@/hooks/useFlightResults/useFlightResults";
import { FlightsSelect } from "../../../FlightPicker/FlightsSelect/FlightsSelect";
import { FieldProps } from "@/core/components/Field";
import { Box, Card } from "@mui/material";
import { DatePicker } from "@/core/components/DatePicker/DatePicker";
import ManualTransportInput from "@/components/custom/AlternativeTransportation/Transportations/ManualTransports/ManualTransportInput/ManualTransportInput";
import { transportMapper } from "@/services/TransportMapper/TransportMapper";
import TransportSelected from "@/components/custom/AlternativeTransportation/Transportations/TransportSelected/TransportSelected";
import {
  DateTimeValidationType,
  SelectedTransport,
  TransportHOCProps,
  TransportRef,
} from "@/core/types/types";
import { useTheme } from "@mui/system";
import getFlatFormValue from "@/core/utils/getFlatFormValue";
import { UseFormWatch } from "react-hook-form/dist/types/form";
import ErrorMessage from "@/core/components/ErrorMessage/ErrorMessage";
import { useTranslations as t } from "@/core/hooks/useTranslations/useTranslations";
import { Loading } from "@/core/components/Loading/Loading";
import { processManualTransport } from "@/core/utils/processManualtransport";

const REF_DISPLAY_NAME = "AlternativeTransportationFlight";

const AlternativeTransportationFlight = forwardRef<
  TransportRef,
  FieldProps & TransportHOCProps
>(({ field }: FieldProps & TransportHOCProps, ref) => {
  const {
    departureDate,
    originAirportData,
    destinationAirportData,
    transportOrder,
    transportType,
    transportsFieldKey,
    disableFutureDates = false,
    defaultTransport,
    onDateUpdate,
  } = field;
  const theme = useTheme();
  const {
    selectedFlightDate,
    selectedFlight,
    flightResults,
    setSelectedFlightDate,
    setSelectedFlight,
    hasError: flightResultsError,
    isLoading,
  } = useFlightResults({
    departureDate,
    originAirport: originAirportData?.iata,
    destinationAirport: destinationAirportData?.iata,
  });
  useImperativeHandle(ref, () => ({
    updateDate: (newDate: string) => {
      if (isEditing) {
        setSelectedFlightDate(newDate);
      }
    },
  }));
  const shouldDepartAfterTime = field.properties?.[DateTimeValidationType.After]
    ? new Date(field.properties?.[DateTimeValidationType.After]).getTime()
    : null;

  const {
    setValue,
    control,
    formState: { errors },
    watch,
    clearErrors,
  }: {
    setValue: UseFormSetValue<FieldValues>;
    control: Control;
    watch: UseFormWatch<FieldValues>;
    formState: {
      errors: any;
    };
    clearErrors: UseFormClearErrors<FieldValues>;
  } = useFormContext();
  const formValues = watch();
  const hasError = !!errors?.[transportsFieldKey]?.[transportOrder];
  const errorMessage =
    errors?.[transportsFieldKey]?.[transportOrder]?.message || "";

  const [enteredTransport, setEnteredTransport] = useState<
    SelectedTransport | null | undefined
  >(undefined);

  const [isEditing, setIsEditing] = useState<boolean>(!defaultTransport);
  const isSelectedTransportReady: boolean = !!(
    selectedFlight ||
    enteredTransport ||
    !!defaultTransport
  );

  useEffect(() => {
    setIsEditing(!defaultTransport);
  }, [defaultTransport]);

  useEffect(() => {
    setValue(field.key, enteredTransport ?? defaultTransport);
  }, [enteredTransport, defaultTransport]);

  const handleFlightSelect = (flight: any) => {
    const mappedTransport = flight
      ? transportMapper.fromSearchResult(flight, transportOrder)
      : flight;

    setSelectedFlight(flight);
    setIsEditing(flight === null);
    setEnteredTransport(mappedTransport);
    if (mappedTransport) {
      const arrivalDate =
        mappedTransport.scheduledArrivalDateTime.split("T")[0];
      onDateUpdate(arrivalDate);
    }

    clearErrors(field.key);
  };

  const handleManualTransportEnter = (transport: any) => {
    const mappedTransportResponse: Promise<
      SelectedTransport | null | undefined
    > = transport
      ? processManualTransport({
          ...transport,
          departureAirportIata: originAirportData.iata,
          arrivalAirportIata: destinationAirportData.iata,
        })
      : transport;
    mappedTransportResponse.then(
      (result: SetStateAction<SelectedTransport | null | undefined>) => {
        setEnteredTransport(result);
        if (result) {
          const arrivalDate = (
            result as SelectedTransport
          ).scheduledArrivalDateTime.split("T")[0];
          onDateUpdate(arrivalDate);
        }
      }
    );
    setIsEditing(false);
    clearErrors(field.key);
  };

  const handleEdit = () => {
    setEnteredTransport(flightResults.length === 0 ? null : undefined);
    setIsEditing(true);
    clearErrors(field.key);
  };

  const validateSelectedFlight = (selectedFlight: SelectedTransport) => {
    if (!selectedFlight) {
      return `validate.${transportsFieldKey}.required.message`;
    }

    const previousTransport = getFlatFormValue(
      formValues,
      `${transportsFieldKey}[${transportOrder - 1}]`
    );

    const departureDateTime =
      selectedFlight.scheduledDepartureDateTime ||
      previousTransport?.actualDepartureDateTime;

    const currentTransportDepartureTime = new Date(departureDateTime).getTime();

    if (
      shouldDepartAfterTime &&
      shouldDepartAfterTime > currentTransportDepartureTime
    ) {
      return `validate.${transportsFieldKey}.notInSequence.message`;
    }

    if (!previousTransport) return true;

    const previousTransportArrivalTime = new Date(
      previousTransport.scheduledArrivalDateTime ??
        previousTransport.actualArrivalDateTime
    ).getTime();

    return (
      currentTransportDepartureTime > previousTransportArrivalTime ||
      `validate.${transportsFieldKey}.notInSequence.message`
    );
  };

  useEffect(() => {
    setSelectedFlight(flightResults.length ? undefined : null);
    setEnteredTransport(flightResults.length ? undefined : null);
  }, [flightResults]);

  if (flightResultsError) return null;

  if (
    !selectedFlightDate ||
    !originAirportData ||
    !destinationAirportData ||
    !flightResults
  )
    return null;

  return (
    <>
      <Card
        sx={{
          boxShadow: "none",
          padding: enteredTransport ? theme.spacing(1, 1, 1, 2) : 2,
        }}
      >
        <Controller
          name={field.key}
          control={control}
          rules={{
            validate: validateSelectedFlight,
          }}
          render={() =>
            isEditing ? (
              <>
                <Box
                  sx={{
                    display: "flex",
                    alignSelf: "center",
                    justifyContent: "center",
                  }}
                  mb={2}
                >
                  <DatePicker
                    value={selectedFlightDate}
                    onPreviousDateSelect={setSelectedFlightDate}
                    onNextDateSelect={setSelectedFlightDate}
                    disableFutureDates={disableFutureDates}
                    showWeekday
                  />
                </Box>
                {isLoading ? (
                  <Box sx={{ display: "flex", flexDirection: "column" }}>
                    <Loading />
                  </Box>
                ) : (
                  <Box>
                    <FlightsSelect
                      field={{ ...field, key: `${field.key}.flightsSelect` }}
                      control={control}
                      errors={errors}
                      flights={flightResults}
                      selectedFlight={selectedFlight}
                      onFlightSelect={handleFlightSelect}
                      required={false}
                    />
                    {/*enteredTransport === null when "manual flight data" option was selected, undefined when selector wasn't touched*/}
                    {enteredTransport === null && (
                      <ManualTransportInput
                        transportOrder={transportOrder}
                        transportType={transportType}
                        onTransportEnter={handleManualTransportEnter}
                        appendedToFlightsList={flightResults.length !== 0}
                        disableFutureDates={disableFutureDates}
                        selectedFlightDate={selectedFlightDate}
                        originAirportData={originAirportData.iata}
                        destinationAirportData={destinationAirportData.iata}
                      />
                    )}
                  </Box>
                )}
              </>
            ) : (
              <>
                {isSelectedTransportReady && (
                  <TransportSelected
                    transport={
                      selectedFlight || enteredTransport || defaultTransport!!
                    }
                    isSelectedFromList={!!selectedFlight}
                    handleEdit={handleEdit}
                  />
                )}
              </>
            )
          }
        />
      </Card>
      {hasError && <ErrorMessage text={t(errorMessage)} />}
    </>
  );
});
AlternativeTransportationFlight.displayName = REF_DISPLAY_NAME;

export { AlternativeTransportationFlight };
